import React, { forwardRef, useImperativeHandle, useState, useRef } from 'react'
import Dialog from '@material-ui/core/Dialog'
import CloseIcon from '@material-ui/icons/Close'
import { useTranslation } from 'react-i18next'
import { useSelector, useDispatch } from 'react-redux'
import Moment from 'moment';
import { extendMoment } from 'moment-range'
import cn from 'classnames'

import { generateResourcesFromEmployees, setCheckedEmployees, getCheckedEmployeeIds, updateAllEmployees } from '../../TimeOff/Calendar/utils'
import { getCompanyEmployeesAll, getPlaces, getCompanyJobTypes, getCompanyCustomCategories, addLogbookEntries, updateLogbookEntry } from '../../../api'
import usePermissions from '../../Core/usePermissions'
import { addSnackbar, dismissSnackbar } from '../../../store/organizationList/actions'

import styles from './styles.module.scss'

import CustomSelect from '../../Core/CustomSelect'
import Label from '../../Core/InputLabel'
import TrashIcon from '../../Icons/TrashIcon'
import RefreshArrow from '../../Icons/RefreshArrow'
import PendingIcon from '../../Icons/PendingIcon'
import PauseIcon from '../../Icons/PauseIcon'

const AttentionIcon = (props) => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={24}
      height={24}
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      aria-hidden="true"
      {...props}
    >
      <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3M12 9v4M12 17h.01" />
    </svg>
  )
}

const RestoreIcon = (props) => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 18.818 18.232"
      width={14}
      height={14}
      {...props}
    >
      <path
        fillOpacity={0.85}
        d="M0 6.572a.88.88 0 0 0 .293.645l5.742 5.635a.91.91 0 0 0 .625.273c.527 0 .87-.352.87-.86a.825.825 0 0 0-.235-.595L4.473 8.926 1.719 6.572 4.473 4.21l2.822-2.744a.825.825 0 0 0 .234-.596c0-.508-.341-.86-.869-.86a.91.91 0 0 0-.625.274L.293 5.918A.885.885 0 0 0 0 6.572Zm8.486 10.791c0 .479.352.87.889.87h2.256c4.365 0 6.826-2.49 6.826-6.231 0-3.73-2.53-6.309-6.982-6.309H4.912L1.68 5.84c-.4.02-.723.322-.723.732 0 .4.322.703.723.723l3.232.146h6.71c3.33 0 5.087 1.836 5.087 4.463 0 2.637-1.758 4.59-5.088 4.59H9.375c-.537 0-.889.39-.889.87Z"
      />
    </svg>
  )
}

const moment = extendMoment(Moment)

const getCustomCategoriesOfItem = (item, allCustomCategories) => {  
  return allCustomCategories.map((cat) => {
    switch (cat.field_type) {
      case 'entry_field':
        return {
          id: cat.id,
          name: cat.name,
          value: item?.custom_category_sessions?.reverse()?.find(s => s.custom_category_id === cat.id)?.value || '',
        }
      case 'dropdown_fields':
        return {
          id: cat.id,
          name: cat.name,
          dropdown_fields: cat.dropdown_fields.map((field) => {
            return {
              ...field,
              value: item?.custom_category_dropdown_session?.reverse()?.find(s => s.custom_category_id === cat.id && s.custom_category_dropdown_field_id === field.id)?.value || '',
            }
          }),
        }
    }
  }).filter(Boolean)
}

const getDuration = (workParts) => {
  if (!workParts.length) {
    return { hours: 0, minutes: 0, asHours: 0, asMinutes: 0 }
  }
  const startedAt = workParts[0].started_at
  const finishedAt = workParts[workParts.length - 1].finished_at
  const duration = moment.duration(finishedAt.diff(startedAt))
  const hours = Math.floor(duration.asHours())
  const minutes = duration.minutes()
  return { hours, minutes, asHours: duration.asHours(), asMinutes: duration.asMinutes() }
}

const getOvertime = (duration, targetDuration) => {
  if (duration > targetDuration) {
    return {
      hours: Math.floor((duration - targetDuration) / 60),
      minutes: Math.round((duration - targetDuration) % 60),
      duration: moment.duration(duration - targetDuration, 'minutes').asMinutes(),
    }
  }
  return {
    hours: 0,
    minutes: 0,
    duration: 0,
  }
}

const getTimeFromMinutes = (minutes) => {
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  return { hours, minutes: remainingMinutes };
}

const hoursOptions = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]
const startTimeOptions = Array.from(moment.range(moment().startOf('day'), moment().endOf('day')).by('hour')).map(h => h.format('HH:mm'))

const permissionsConfig = [
  {
    name: 'places',
    module: 'create_places',
  },
  {
    name: 'jobs',
    module: 'create_jobs',
  },
  {
    name: 'comments_photo',
    module: 'comments_photo',
  },
  {
    name: 'logbook_edit_categories',
    permission: 'logbook_can_edit_categories',
  },
]

const EMPTY_FORM = {
  additional_bonus: '',
  bonus_comment: '',
  comment: '',
  manager_comment: '',
  work_parts: [{
    started_at: moment().set({ hour: 8, minute: 0, seconds: 0, milliseconds: 0 }),
    finished_at: moment().set({ hour: 9, minute: 0, seconds: 0, milliseconds: 0 }).add(8, 'hours'),
    removed: false,
  }],
  break_parts: [],
  custom_categories: [],
}

const LogbookEntryForm = forwardRef(({companyId, onSuccess}, ref) => {
  const { t } = useTranslation()
  const permissions = usePermissions(permissionsConfig)
  const dispatch = useDispatch()

  const company = useSelector(({company}) => company.companyInfo)
  const journal = useSelector(({settings}) => settings.journal)

  const [form, setForm] = useState(EMPTY_FORM)
  const [open, setOpen] = useState(false)
  const [confirmOvertime, setConfirmOvertime] = useState(false)
  const [processing, setProcessing] = useState(false)
  const [loading, setLoading] = useState(false)

  const timeDetailsColumnRef = useRef(null)
  const initFormRef = useRef(null)
  const dataRef = useRef(null)

  const [{employees, places, jobTypes}, setData] = useState({
    employees: [],
    places: [],
    jobTypes: []
  })

  const isEdit = Boolean(form.id)
  const startTime = form.work_parts[0].started_at.format('HH:mm')
  const finishTime = form.work_parts[form.work_parts.length - 1].finished_at.format('HH:mm')

  const duration = getDuration(form.work_parts)
  const breakDuration = getDuration(form.break_parts)

  const targetOvertime = getTimeFromMinutes(journal.warning_on_create_duration || 60*9)
  const overtime = getOvertime(duration.asMinutes, journal.warning_on_create_duration)
  const currentSelected = { employees: getCheckedEmployeeIds(employees), places: getCheckedEmployeeIds(places), jobTypes: getCheckedEmployeeIds(jobTypes) }

  const hasChanges = (JSON.stringify(form) !== JSON.stringify(initFormRef.current)) || (JSON.stringify(dataRef.current) !== JSON.stringify(currentSelected))

  useImperativeHandle(ref, () => ({
    show: async (data) => {
      setOpen(true)
      const initalData = await init(data ? {employee_id: data.employee_id, place_id: data.place_id, job_type_id: data.job_type_id} : {})
      if (data) {
        const [comment] = data.comments
        initFormRef.current = {
          ...EMPTY_FORM,
          id: data.id,
          session_id: data.session_id,
          additional_bonus: data.additional_bonus || '',
          comment: comment?.comment || '',
          work_parts: data.works.map(w => {
            return {
              ...w,
              started_at: moment(w.started_at),
              finished_at: moment(w.finished_at)
            }
          }),
          break_parts: data.breaks.map(b => {
            return {
              ...b,
              started_at: moment(b.started_at),
              finished_at: moment(b.finished_at)
            }
          }),
          custom_categories: getCustomCategoriesOfItem(data, initalData.categories)
        }
        setForm(initFormRef.current)
        return
      }
      initFormRef.current = {...EMPTY_FORM, custom_categories: getCustomCategoriesOfItem(null, initalData.categories) }
      setForm(initFormRef.current)
    }
  }))

  const init = async (params = {}) => {
    setLoading(true)
    const [employeesRes, placesRes, jobTypesRes, customCategoriesRes] = await Promise.all([
      getCompanyEmployeesAll(companyId),
      getPlaces(companyId),
      getCompanyJobTypes(companyId),
      getCompanyCustomCategories(companyId)
    ])
    const employeesAll = Array.isArray(employeesRes?.users) ? generateResourcesFromEmployees(employeesRes.users) : []
    const places = Array.isArray(placesRes) ? placesRes.map(place => ({title: place.name, id: place.id, isEmployee: true, checked: place.id === params.place_id})) : []
    const jobTypes = Array.isArray(jobTypesRes) ? jobTypesRes.map(jobType => ({title: jobType.title, id: jobType.id, isEmployee: true, checked: jobType.id === params.job_type_id}))  : []
    const employees = params.employee_id ? setCheckedEmployees(employeesAll, [params.employee_id], true) : employeesAll
    dataRef.current = {
      employees: getCheckedEmployeeIds(employees),
      places: getCheckedEmployeeIds(places),
      jobTypes: getCheckedEmployeeIds(jobTypes),
    }
    setData({
      employees: employees,
      places: places,
      jobTypes: jobTypes,
    })
    const categories = Array.isArray(customCategoriesRes) ? customCategoriesRes.filter(cat => cat.start_work || cat.finish_work) : []
    setLoading(false)
    return {
      employees,
      places,
      jobTypes,
      categories,
    }
  }

  const close = () => {
    setOpen(false)
  }

  const handleRestore = () => {
    setForm(initFormRef.current)
    setData({
      employees: setCheckedEmployees(updateAllEmployees(employees, false), dataRef.current.employees, true),
      places: setCheckedEmployees(updateAllEmployees(places, false), dataRef.current.places, true),
      jobTypes: setCheckedEmployees(updateAllEmployees(jobTypes, false), dataRef.current.jobTypes, true)
    })
  }

  const handleChangeSelect = (type) => (data) => {
    setData(prev => ({ ...prev, [type]: data }))
  }

  const handleInput = (field) => ({target: {value}}) => {
    setForm(prev => ({ ...prev, [field]: value }))
  }

  const handleChangeStartTime = (value) => () => {
    const [hours, minutes] = value.split(':').map(Number)
    setForm(prev => {
      const updatedWorkParts = [...prev.work_parts]
      const startedAt = moment(updatedWorkParts[0].started_at).set('hours', hours).set('minutes', minutes)
      updatedWorkParts[0] = {
        ...updatedWorkParts[0],
        started_at: startedAt,
        finished_at: startedAt.clone().add(moment(updatedWorkParts[0].finished_at).diff(moment(updatedWorkParts[0].started_at)), 'milliseconds')
      }
      return { ...prev, work_parts: updatedWorkParts }
    })
  }

  const handleChangeDuration = (value) => () => {
    setForm(prev => {
      const updatedWorkParts = [...prev.work_parts]
      const start = updatedWorkParts[0].started_at
      const newFinish = start.clone().add(value, 'hours')
      updatedWorkParts[updatedWorkParts.length - 1] = {
        ...updatedWorkParts[updatedWorkParts.length - 1],
        finished_at: newFinish
      }
      return { ...prev, work_parts: updatedWorkParts }
    })
  }

  const handleInputNumber = (field) => ({target: {value}}) => {
    const sanitizedValue = value.replace(/[^0-9.]/g, '')
    const decimalIndex = sanitizedValue.indexOf('.')
    const formattedValue = decimalIndex === -1
      ? sanitizedValue
      : sanitizedValue.slice(0, decimalIndex + 1) + sanitizedValue.slice(decimalIndex + 1).replace(/\./g, '')
    setForm(prev => ({ ...prev, [field]: formattedValue }))
  }

  const handleChangeWorkPart = (index, field) => ({target: {value}}) => {
    setForm(prev => {
      const updatedWorkParts = [...prev.work_parts]
      updatedWorkParts[index] = {
        ...updatedWorkParts[index],
        [field]: moment(value).set('seconds', 0).set('milliseconds', 0)
      }
      return { ...prev, work_parts: updatedWorkParts }
    })
  }

  const handleChangeBreakPart = (index, field) => ({target: {value}}) => {
    setForm(prev => {
      const updatedWorkParts = [...prev.break_parts]
      updatedWorkParts[index] = {
        ...updatedWorkParts[index],
        [field]: moment(value).set('seconds', 0).set('milliseconds', 0)
      }
      return { ...prev, break_parts: updatedWorkParts }
    })
  }

  const handleRemoveWorkPart = (index) => () => {
    setForm(prev => {
      return {
        ...prev,
        work_parts: prev.work_parts.map((wp, i) => {
          return i === index ? { ...wp, removed: !wp.removed } : wp
        })
      }
    })
  }

  const handleRemoveBreakPart = (index) => () => {
    setForm(prev => {
      return {
        ...prev,
        break_parts: prev.break_parts.map((bp, i) => {
          return i === index ? { ...bp, removed: !bp.removed } : bp
        })
      }
    })
  }

  const handleChangeCategory = (categoryId) => ({target: {value}}) => {
    setForm(prev => {
      const updatedCustomCategories = prev.custom_categories.map(cat => {
        if (cat.id === categoryId) {
          return { ...cat, value }
        }
        return cat
      })
      return { ...prev, custom_categories: updatedCustomCategories }
    })
  }

  const handleChangeCategoryDropdown = (categoryId, fieldId) => ({target: {value}}) => {
    setForm(prev => {
      const updatedCustomCategories = prev.custom_categories.map(cat => {
        if (cat.id === categoryId) {
          const updatedFields = cat.dropdown_fields.map(field => {
            if (field.id === fieldId) {
              return { ...field, value }
            }
            return field
          })
          return { ...cat, dropdown_fields: updatedFields }
        }
        return cat
      })
      return { ...prev, custom_categories: updatedCustomCategories }
    })
  }

  const handleSubmit = async () => {
    const categoryValues = form.custom_categories.filter(cat => !Array.isArray(cat.dropdown_fields)).reduce((acc, cat) => ({
      ...acc,
      [`custom_category_${cat.id}`]: cat.value
    }), {})
    const employeeIds = getCheckedEmployeeIds(employees)
    const post = {
      employee_ids: employeeIds,
      employee_id: employeeIds[0],
      place_id: getCheckedEmployeeIds(places)[0],
      job_type_id: getCheckedEmployeeIds(jobTypes)[0],
      works: form.work_parts.map(wp => ({
        ...wp,
        started_at: wp.started_at.format('YYYY-MM-DD HH:mm:ss'),
        finished_at: wp.finished_at.format('YYYY-MM-DD HH:mm:ss'),
        delete: wp.removed
      })),
      comment: form.comment,
      manager_comment: form.manager_comment,
      additional_bonus: form.additional_bonus,
      bonus_comment: form.bonus_comment,
      id: form.id,
      session_id: form.session_id,
      breaks: form.break_parts.map(bp => ({
        ...bp,
        started_at: bp.started_at.format('YYYY-MM-DD HH:mm:ss'),
        finished_at: bp.finished_at.format('YYYY-MM-DD HH:mm:ss'),
        delete: bp.removed
      })),
      custom_category_dropdown_session: form.custom_categories.filter(cat => Array.isArray(cat.dropdown_fields)).reduce((acc, cat) => {
        return [
          ...acc,
          ...cat.dropdown_fields.map(field => ({
            ...field,
            custom_category_id: cat.id,
          }))
        ]
      }, []),
      ...categoryValues,
    }
    setProcessing(true)
    const res = await (isEdit ? updateLogbookEntry(companyId, post) : addLogbookEntries(companyId, post))
    setProcessing(false)
    if (res) {
      handleSuccess()
    }
  }

  const handleSuccess = () => {
    onSuccess()
    dispatch(addSnackbar('Updated logbook successfully', 'success'))
    setTimeout(() => dispatch(dismissSnackbar()), 2000)
    close()
  }

  return (
    <Dialog
      onClose={close}
      open={open}
      maxWidth={'xl'}
      // PaperProps={{ style: { maxWidth: 1080, width: '100%' } }}
    >
      <div className={styles.container}>
        <div className={styles.header}>
          <div className={styles.title}>
            {isEdit ? t('Edit Entry') : t('New Entry')}
          </div>
          <button type="button" className={styles.closeButton} onClick={close}>
            <CloseIcon />
          </button>
        </div>
        <div className={styles.content} aria-busy={loading}>
          {loading && (
            <div className={styles.skeletonColumns} aria-hidden="true">
              <div className={styles.columnWrapper}>
                <div className={styles.skeletonField}>
                  <div className={styles.skeletonLabel} />
                  <div className={styles.skeletonControl} />
                </div>
                <div className={styles.skeletonField}>
                  <div className={styles.skeletonLabel} />
                  <div className={styles.skeletonControl} />
                </div>
                <div className={styles.skeletonTotal} />
                <div className={styles.skeletonTotal} />
                <div className={styles.skeletonTotal} />
                <div className={styles.skeletonField}>
                  <div className={styles.skeletonLabel} />
                  <div className={styles.skeletonTextarea} />
                </div>
                <div className={styles.skeletonField}>
                  <div className={styles.skeletonLabel} />
                  <div className={styles.skeletonTextarea} />
                </div>
              </div>
              <div className={styles.columnWrapper}>
                <div className={styles.skeletonCard}>
                  <div className={styles.skeletonLabel} />
                  <div className={styles.skeletonGrid}>
                    <div className={styles.skeletonButton} />
                    <div className={styles.skeletonButton} />
                    <div className={styles.skeletonButton} />
                    <div className={styles.skeletonButton} />
                    <div className={styles.skeletonButton} />
                    <div className={styles.skeletonButton} />
                    <div className={styles.skeletonButton} />
                    <div className={styles.skeletonButton} />
                    <div className={styles.skeletonButton} />
                    <div className={styles.skeletonButton} />
                    <div className={styles.skeletonButton} />
                    <div className={styles.skeletonButton} />
                  </div>
                  <div className={styles.skeletonLabel} />
                  <div className={styles.skeletonGrid}>
                    <div className={styles.skeletonButton} />
                    <div className={styles.skeletonButton} />
                    <div className={styles.skeletonButton} />
                    <div className={styles.skeletonButton} />
                    <div className={styles.skeletonButton} />
                    <div className={styles.skeletonButton} />
                    <div className={styles.skeletonButton} />
                    <div className={styles.skeletonButton} />
                    <div className={styles.skeletonButton} />
                    <div className={styles.skeletonButton} />
                    <div className={styles.skeletonButton} />
                    <div className={styles.skeletonButton} />
                  </div>
                </div>
                <div className={styles.skeletonField}>
                  <div className={styles.skeletonLabel} />
                  <div className={styles.skeletonPart} />
                </div>
              </div>
              <div className={styles.columnWrapper}>
                <div className={styles.skeletonCard}>
                  <div className={styles.skeletonLabel} />
                  <div className={styles.skeletonField}>
                    <div className={styles.skeletonLabel} />
                    <div className={styles.skeletonTextarea} />
                  </div>
                  <div className={styles.skeletonField}>
                    <div className={styles.skeletonLabel} />
                    <div className={styles.skeletonTextarea} />
                  </div>
                </div>
              </div>
            </div>
          )}
          <div className={styles.columnWrapper}>
            <div className={styles.column}>
              {
                !isEdit
                  ? <div className={styles.formInput}>
                      <Label text={t('Employees')} />
                      <CustomSelect
                        items={employees}
                        onChange={handleChangeSelect('employees')} />
                    </div>
                  : null
              }
              {
                permissions.places && places.length
                  ? <div className={styles.formInput}>
                      <Label text={t('Place')} />
                      <CustomSelect
                        items={places}
                        multipleSelect={false}
                        singleSelect
                        onChange={handleChangeSelect('places')} />
                    </div>
                  : null
              }
              {
                permissions.jobs && jobTypes.length
                  ? <div className={styles.formInput}>
                      <Label text={t('Job')} />
                      <CustomSelect
                        items={jobTypes}
                        multipleSelect={false}
                        singleSelect
                        onChange={handleChangeSelect('jobTypes')} />
                    </div>
                  : null
              }
              <div className={cn(styles.totalCard, {[styles.warningLight]: overtime.duration > 0 && journal?.use_warning_on_create})}>
                <div className={styles.label}>{t('From')}</div>
                <div className={styles.value}>{startTime}</div>
                <div className={styles.label} style={{marginLeft: 'auto'}}>{t('To')}</div>
                <div className={styles.value}>{finishTime}</div>
              </div>
              <div className={cn(styles.totalCard, {[styles.warning]: overtime.duration > 0 && journal?.use_warning_on_create})}>
                <PendingIcon className={styles.icon} />
                <div className={styles.label} style={{color: '#0085ff'}}>{t('Total hours')}</div>
                <div className={styles.value} style={{marginLeft: 'auto'}}>
                  {duration.hours}<span>h</span> {duration.minutes}<span>m</span>
                </div>
              </div>
              <div className={cn(styles.totalCard, styles.break)}>
                <PauseIcon className={styles.icon} />
                <div className={styles.label} style={{color: '#ff9d00'}}>{t('On break')}</div>
                <div className={styles.value} style={{marginLeft: 'auto'}}>
                  {breakDuration.hours}<span>h</span> {breakDuration.minutes}<span>m</span>
                </div>
              </div>
              {
                permissions.comments_photo && journal.end_day_comment
                  ? <div className={styles.formInput}>
                      <Label text={t('Comment')} />
                      <textarea
                        className={styles.input}
                        value={form.comment}
                        placeholder={t('Add comment here')}
                        onChange={handleInput('comment')} />
                    </div>
                  : null
              }
              {
                journal.additional_bonus
                  ? <>
                      <div className={styles.formInput}>
                        <Label text={t('Bonus')} />
                        <div className={styles.inputWrapper}>
                          <input
                            type="text"
                            className={styles.input}
                            value={form.additional_bonus}
                            placeholder="0"
                            onChange={handleInputNumber('additional_bonus')} />
                          <div className={styles.inputAddon}>{company.currency_symbol}</div>
                        </div>
                      </div>
                      <div className={styles.formInput}>
                        <Label text={t('Bonus comment')} />
                        <textarea
                          className={styles.input}
                          value={form.bonus_comment}
                          placeholder={t('If needed make a note why this action is taken.')}
                          onChange={handleInput('bonus_comment')} />
                      </div>
                    </>
                  : null
              }
              <div className={styles.formInput}>
                <Label text={t('Administrator note (optional)')} />
                <textarea
                  className={styles.input}
                  value={form.manager_comment}
                  placeholder={t('If needed make a note why this action is taken.')}
                  onChange={handleInput('manager_comment')} />
              </div>
            </div>
          </div>
          <div className={styles.columnWrapper}>
            <div className={styles.column} ref={timeDetailsColumnRef}>
              <div className={styles.card}>
                <div className={styles.sectionHeader}>
                  <div className={styles.sectionTitle}>{t('Set hours')}</div>
                  <button type="button" className={styles.restoreButton} disabled={!hasChanges} onClick={handleRestore}>
                    <RestoreIcon />
                    {t('Restore')}
                  </button>
                </div>
                <div className={styles.buttonsWrapper}>
                  {
                    hoursOptions.map(value => {
                      return (
                        <button
                          key={value}
                          type="button"
                          className={cn(styles.button, {[styles.active]: duration.asHours === value})}
                          onClick={handleChangeDuration(value)}>
                          {value}
                        </button>
                      )
                    })
                  }
                </div>
                <div className={styles.sectionTitle}>{t('Start time')}</div>
                <div className={styles.buttonsWrapper}>
                  {
                    startTimeOptions.map(value => {
                      return (
                        <button
                          key={value}
                          type="button"
                          className={cn(styles.button, {[styles.active]: startTime === value})}
                          onClick={handleChangeStartTime(value)}>
                          {value}
                        </button>
                      )
                    })
                  }
                </div>
              </div>
              <div>
                <div className={styles.sectionTitle}>{t('Work parts')}</div>
                <div className={styles.parts}>
                  {
                    form.work_parts.map((workPart, i) => {
                      return (
                        <div key={i} className={cn(styles.workPart, {[styles.removed]: workPart.removed})}>
                          <div className={styles.label}>{t('Work part {{index}}', {index: i + 1})}</div>
                          <div className={styles.inputsWrapper}>
                            <input
                              type="datetime-local"
                              value={workPart.started_at.format('YYYY-MM-DD HH:mm')}
                              onChange={handleChangeWorkPart(i, 'started_at')} />
                            <input
                              type="datetime-local"
                              value={workPart.finished_at.format('YYYY-MM-DD HH:mm')}
                              onChange={handleChangeWorkPart(i, 'finished_at')} />
                          </div>
                          {
                            isEdit
                              ? <button type="button" className={styles.deleteButton} onClick={handleRemoveWorkPart(i)}>
                                  {
                                    workPart.removed
                                      ? <RefreshArrow className={styles.refreshIcon} />
                                      : <TrashIcon className={styles.trashIcon} />
                                  }
                                </button>
                              : null
                          }
                        </div>
                      )
                    })
                  }
                </div>
              </div>
              {
                form.break_parts.length
                  ? <div>
                      <div className={styles.sectionTitle}>{t('Break parts')}</div>
                      <div className={styles.parts}>
                        {
                          form.break_parts.map((workPart, i) => {
                            return (
                              <div key={i} className={cn(styles.workPart, styles.break, {[styles.removed]: workPart.removed})}>
                                <div className={styles.label}>{t('Break part {{index}}', {index: i + 1})}</div>
                                <div className={styles.inputsWrapper}>
                                  <input
                                    type="datetime-local"
                                    value={workPart.started_at.format('YYYY-MM-DD HH:mm')}
                                    onChange={handleChangeBreakPart(i, 'started_at')} />
                                  <input
                                    type="datetime-local"
                                    value={workPart.finished_at.format('YYYY-MM-DD HH:mm')}
                                    onChange={handleChangeBreakPart(i, 'finished_at')} />
                                </div>
                                {
                                  isEdit
                                    ? <button type="button" className={styles.deleteButton} onClick={handleRemoveBreakPart(i)}>
                                        {
                                          workPart.removed
                                            ? <RefreshArrow className={styles.refreshIcon} />
                                            : <TrashIcon className={styles.trashIcon} />
                                        }
                                      </button>
                                    : null
                                }
                              </div>
                            )
                          })
                        }
                      </div>
                    </div>
                  : null
              }
              {
                overtime.duration > 0 && journal?.use_warning_on_create
                  ? <div className={styles.warningCard}>
                      <div className={styles.warningCardHeader}>
                        <AttentionIcon />
                        <div>{t('Exceeds {{hours}}h {{minutes}}min daily limit', {hours: targetOvertime.hours, minutes: targetOvertime.minutes})}</div>
                      </div>
                      <div>{t('This entry is {{overtimeHours}}h {{overtimeMinutes}}min over the recommended {{targetHours}}h {{targetMinutes}}min maximum.', {overtimeHours: overtime.hours, overtimeMinutes: overtime.minutes, targetHours: targetOvertime.hours, targetMinutes: targetOvertime.minutes})}</div>
                      <div className={styles.warningCardFooter}>
                        <input type="checkbox" name="confirmOvertime" checked={confirmOvertime} onChange={e => setConfirmOvertime(!confirmOvertime)} />
                        {t('I confirm this overtime entry')}
                      </div>
                    </div>
                  : null
              }
            </div>
          </div>
          {
            permissions.logbook_edit_categories && form.custom_categories.length
              ? <div className={styles.columnWrapper}>
                  <div className={styles.column}>
                    <div className={styles.card}>
                      <div className={styles.sectionTitle}>{t('Additional information')}</div>
                      <div className={styles.categories}>
                        {
                          form.custom_categories.map((category, i) => {
                            if (Array.isArray(category.dropdown_fields)) {
                              return (
                                <div style={{display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 16}} key={i}>
                                  <Label text={category.name} />
                                  {
                                    category.dropdown_fields.map((field) => {
                                      return (
                                        <div className={styles.dropdownField} key={field.id}>
                                          <div className={styles.label}>{field.name}</div>
                                          <textarea
                                            className={styles.input}
                                            onChange={handleChangeCategoryDropdown(category.id, field.id)}
                                            name={field.id}
                                            value={field.value}
                                          />
                                        </div>
                                      )
                                    })
                                  }
                                </div>
                              )
                            }
                            const key = `custom_category_${category.id}`
                            return (
                              <div key={i} className={styles.formInput}>
                                <Label text={category.name} />
                                <textarea
                                  className={styles.input}
                                  onChange={handleChangeCategory(category.id)}
                                  name={key}
                                  value={category.value}
                                />
                              </div>
                            )
                          })
                        }
                      </div>
                    </div>
                  </div>
                </div>
              : null
          }
        </div>
        <div className={styles.footer}>
          <button type="button" className={styles.cancelButton} onClick={close}>
            { t('Cancel') }
          </button>
          <button
          type="button"
            disabled={loading || (journal?.use_warning_on_create && !confirmOvertime && overtime.duration > 0) || processing}
            className={cn(styles.submitButton, {[styles.warning]: journal?.use_warning_on_create && overtime.duration > 0})}
            onClick={handleSubmit}>
            { journal?.use_warning_on_create && overtime.duration > 0 ? t('Save anyway (overtime)') : t('Save & Close') }
          </button>
        </div>
      </div>
    </Dialog>
  )
})

export default LogbookEntryForm
